import { generate12WordMnemonic as generateMnemonic, encryptKeyStore, decryptKeyStore, loadWalletFromMnemonic } from './wallet.js';
import { getBalance, getUtxos, broadcastTx } from './api.js';
import { createTransaction } from './bitcoin-ops.js';

// Simple SPA State & Router
const state = {
    wallet: null,
    utxos: [],
    balance: 0,
    pendingOutgoing: 0,
    lastConfirmedBalance: 0,
    pollingInterval: null,

    // Swap State
    swapCurrencies: [],
    swapEstimateTimeout: null,
    swapActiveId: null,
    swapTrackerInterval: null,
    activeSwaps: [] // Store array of {id, fromAmount, fromCurrency, toCurrency} metadata
};

// ===== Toast Notification System =====
function showToast(message, type = 'info', duration = 2500) {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.animationDuration = `${duration}ms`;
    container.appendChild(toast);
    setTimeout(() => toast.remove(), duration);
}

// ===== Animated Balance Counter =====
function animateBalance(el, targetValue) {
    const start = parseFloat(el.textContent) || 0;
    const diff = targetValue - start;
    if (Math.abs(diff) < 0.000000005) { el.textContent = targetValue.toFixed(8); return; }
    const steps = 30;
    let step = 0;
    const interval = setInterval(() => {
        step++;
        const ease = 1 - Math.pow(1 - step / steps, 3); // cubic ease-out
        el.textContent = (start + diff * ease).toFixed(8);
        if (step >= steps) { el.textContent = targetValue.toFixed(8); clearInterval(interval); }
    }, 16);
}

// ===== Confetti Burst =====
function spawnConfetti() {
    const colors = ['#E8B923', '#f1cd55', '#00e5ff', '#4ade80', '#fff'];
    for (let i = 0; i < 30; i++) {
        const piece = document.createElement('div');
        piece.className = 'confetti-piece';
        piece.style.left = (40 + Math.random() * 20) + 'vw';
        piece.style.top = (20 + Math.random() * 10) + 'vh';
        piece.style.background = colors[Math.floor(Math.random() * colors.length)];
        piece.style.animationDuration = (1 + Math.random() * 1) + 's';
        piece.style.animationDelay = (Math.random() * 0.3) + 's';
        document.body.appendChild(piece);
        setTimeout(() => piece.remove(), 2500);
    }
}

// ===== Button Loading Helper =====
function btnLoading(btn, loading) {
    if (loading) btn.classList.add('loading');
    else btn.classList.remove('loading');
}

// Global App Object accessible from HTML onclick handlers
window.app = {

    // Auth & Wallet Setup
    async generateWallet() {
        const mnemonic = await generateMnemonic();
        document.getElementById('auth-state-new').classList.add('hidden');
        document.getElementById('auth-state-encrypt').classList.remove('hidden');

        const mbox = document.getElementById('mnemonic-display');
        mbox.innerHTML = mnemonic.split(' ').map((w, i) => `<span>${i + 1}. ${w}</span>`).join(' &nbsp;&nbsp;');

        // Temporarily store it
        window._tempMnemonic = mnemonic;
    },

    async importWallet() {
        const words = document.getElementById('import-mnemonic').value.trim();
        if (words.split(' ').length < 12) {
            alert("Invalid mnemonic");
            return;
        }
        window._tempMnemonic = words;
        document.getElementById('auth-state-new').classList.add('hidden');
        document.getElementById('auth-state-encrypt').classList.remove('hidden');
        document.getElementById('mnemonic-display').innerHTML = `<i>Restoring Wallet...</i>`;
    },

    async encryptAndSave() {
        const pwd = document.getElementById('wallet-password').value;
        if (!pwd || pwd.length < 6) {
            alert("Password too short");
            return;
        }

        try {
            await encryptKeyStore(window._tempMnemonic, pwd);
            delete window._tempMnemonic;

            // Decrypt immediately to load into state
            const decrypted = await decryptKeyStore(pwd);
            await this.loadWalletData(decrypted);

        } catch (e) {
            alert("Encryption failed: " + e.message);
        }
    },

    async login() {
        const pwd = document.getElementById('login-password').value;
        if (!pwd) return;

        try {
            const mnemonic = await decryptKeyStore(pwd);
            if (!mnemonic) throw new Error("Incorrect Password");
            await this.loadWalletData(mnemonic);
        } catch (e) {
            showToast(e.message, 'error');
        }
    },

    // View Navigation
    switchView(viewId) {
        document.querySelectorAll('.view').forEach(v => v.classList.add('hidden'));
        document.getElementById('view-' + viewId).classList.remove('hidden');

        // Dynamic Network Badge Label
        const badge = document.getElementById('network-badge');
        if (badge) {
            badge.innerHTML = viewId === 'swap' ? '🟢 DEX' : '🟢 ITC';
        }

        // Clear existing polling
        if (state.pollingInterval) {
            clearInterval(state.pollingInterval);
            state.pollingInterval = null;
        }

        if (viewId === 'dashboard') {
            this.refreshBalance();
            // Auto refresh every 10 seconds
            state.pollingInterval = setInterval(() => this.refreshBalance(), 10000);
        }
    },

    showDashboard() { this.switchView('dashboard'); },
    showSend() {
        document.getElementById('tx-preview').classList.add('hidden');
        this.switchView('send');
    },

    showSwap() {
        this.switchView('swap');
        // Pre-fill ITC origin address if loading swap to ITC later
        if (state.swapCurrencies.length === 0) {
            this.loadSwapCurrencies();
        }

        this.renderActiveSwaps();

        // Delay init to ensure DOM is visible
        setTimeout(() => MarketChartManager.init(), 50);

        document.getElementById('swap-from-currency').addEventListener('change', () => {
            this.updateSwapMinAmount();
            MarketChartManager.updatePairs();
        });
        document.getElementById('swap-to-currency').addEventListener('change', () => {
            this.updateSwapMinAmount();
            MarketChartManager.updatePairs();
        });

        // Debounce estimate
        document.getElementById('swap-from-amount').addEventListener('input', () => {
            if (state.swapEstimateTimeout) clearTimeout(state.swapEstimateTimeout);
            state.swapEstimateTimeout = setTimeout(() => this.getSwapEstimate(), 500);
        });
    },

    showReceive() {
        if (!state.wallet) return;
        const addr = state.wallet.address;
        document.getElementById('receive-address').innerText = addr;

        const qrContainer = document.getElementById('qrcode');
        qrContainer.innerHTML = '';
        new QRCode(qrContainer, {
            text: addr,
            width: 180,
            height: 180,
            colorDark: "#000000",
            colorLight: "#ffffff",
            correctLevel: QRCode.CorrectLevel.M
        });

        this.switchView('receive');
    },

    copyAddress() {
        navigator.clipboard.writeText(state.wallet.address);
        showToast('✓ Address copied!', 'success');
    },

    // Multi-Address & History

    async showAddresses() {
        if (!state.wallet) return;
        this.switchView('addresses');

        const container = document.getElementById('all-addresses-list');
        const explorer = 'https://explorer.interchained.org';
        const allAddrs = state.wallet.addresses;

        // Show shimmer while loading
        let shimmer = '';
        for (let i = 0; i < 5; i++) shimmer += '<div class="skeleton-card skeleton"><div class="skeleton-line skeleton-line-long skeleton"></div><div class="skeleton-line skeleton-line-short skeleton"></div></div>';
        container.innerHTML = shimmer;

        // Single batch call for all balances
        try {
            const calls = allAddrs.map(a => ({
                method: "blockchain.scripthash.get_balance",
                params: [a.scriptHash]
            }));
            const res = await fetch('/api/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ calls })
            });
            if (res.ok) {
                const data = await res.json();
                data.results.forEach((bal, i) => {
                    if (bal) allAddrs[i].balance = (bal.confirmed || 0) + (bal.unconfirmed || 0);
                });
            }
        } catch (e) {
            console.error('Batch balance fetch failed:', e);
        }

        let html = '';
        for (const addr of allAddrs) {
            const balITC = (addr.balance / 100000000).toFixed(8);
            const hasBalance = addr.balance > 0;
            const borderClass = hasBalance ? 'border-gold' : '';
            const shortAddr = addr.address.slice(0, 10) + '...' + addr.address.slice(-8);
            const chainLabel = addr.chain === 'external' ? `Receive #${addr.index}` : `Change #${addr.index}`;

            html += `
                <div class="card glass p-4 mb-4 ${borderClass}" style="padding: 16px;">
                    <div class="flex justify-between items-center">
                        <div>
                            <p class="text-sm text-muted">${chainLabel}</p>
                            <a href="${explorer}/address/${addr.address}" target="_blank" 
                               class="text-sm" style="color: var(--cyan); text-decoration: none; font-family: monospace;">
                                ${shortAddr} ↗
                            </a>
                        </div>
                        <div class="text-right">
                            <p class="${hasBalance ? 'text-gold' : 'text-muted'}" style="font-weight: 600;">${balITC}</p>
                            <p class="text-sm text-muted">ITC</p>
                        </div>
                    </div>
                </div>
            `;
        }

        if (!html) html = '<p class="text-sm text-muted">No addresses found.</p>';
        container.innerHTML = html;
    },

    async showHistory() {
        if (!state.wallet) return;
        this.switchView('history');

        const container = document.getElementById('tx-history-list');
        const explorer = 'https://explorer.interchained.org';
        const allAddrs = state.wallet.addresses;
        const myAddressSet = new Set(allAddrs.map(a => a.address));

        // Show shimmer skeleton loading
        let shimmerHtml = '';
        for (let i = 0; i < 5; i++) {
            shimmerHtml += '<div class="skeleton-card skeleton"><div class="skeleton-line skeleton-line-long skeleton"></div><div class="skeleton-line skeleton-line-medium skeleton"></div><div class="skeleton-line skeleton-line-short skeleton"></div></div>';
        }
        container.innerHTML = shimmerHtml;

        // BATCH 1: Fetch history for all addresses in one call
        const allTxs = new Map();
        try {
            const historyCalls = allAddrs.map(a => ({
                method: "blockchain.scripthash.get_history",
                params: [a.scriptHash]
            }));
            const res = await fetch('/api/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ calls: historyCalls })
            });
            if (res.ok) {
                const data = await res.json();
                data.results.forEach((history, i) => {
                    if (history && Array.isArray(history)) {
                        for (const tx of history) {
                            if (!allTxs.has(tx.tx_hash)) {
                                allTxs.set(tx.tx_hash, { ...tx, addresses: [allAddrs[i].address] });
                            } else {
                                allTxs.get(tx.tx_hash).addresses.push(allAddrs[i].address);
                            }
                        }
                    }
                });
            }
        } catch (e) {
            console.error('Batch history fetch failed:', e);
        }

        // Sort by height (newest first), unconfirmed at top
        const txList = Array.from(allTxs.values());
        txList.sort((a, b) => {
            if (a.height <= 0 && b.height > 0) return -1;
            if (b.height <= 0 && a.height > 0) return 1;
            return (b.height || 0) - (a.height || 0);
        });

        if (txList.length === 0) {
            container.innerHTML = '<p class="text-sm text-muted">No transactions found.</p>';
            return;
        }

        // BATCH 2: Fetch tx details for enrichment (max 20)
        const txsToEnrich = txList.slice(0, 20);
        try {
            const txCalls = txsToEnrich.map(t => ({
                method: "blockchain.transaction.get",
                params: [t.tx_hash, true]
            }));
            const res = await fetch('/api/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ calls: txCalls })
            });
            if (res.ok) {
                const data = await res.json();
                data.results.forEach((txData, i) => {
                    if (txData) txsToEnrich[i].details = txData;
                });
            }
        } catch (e) {
            console.error('Batch tx detail fetch failed:', e);
        }

        // Render enriched history
        let html = '';
        for (const tx of txList) {
            const shortTxid = tx.tx_hash.slice(0, 10) + '...' + tx.tx_hash.slice(-6);
            const confirmed = tx.height > 0;
            const statusBadge = confirmed
                ? `<span style="color: #4ade80; font-size: 0.7rem;">✓ Block ${tx.height}</span>`
                : `<span style="color: var(--gold); font-size: 0.7rem;">⏳ Unconfirmed</span>`;

            // Determine direction and amount from tx details
            let direction = '?';
            let amountITC = '—';
            let dirBadge = '';

            if (tx.details) {
                const d = tx.details;

                let receivedSats = 0;
                let sentToOthersSats = 0;

                if (d.vout) {
                    for (const vout of d.vout) {
                        const outAddr = vout.scriptPubKey?.address || vout.scriptPubKey?.addresses?.[0] || '';
                        const valueSats = Math.round((vout.value || 0) * 100000000);

                        if (myAddressSet.has(outAddr)) {
                            receivedSats += valueSats;
                        } else {
                            sentToOthersSats += valueSats;
                        }
                    }
                }

                // Check if any input spends from a tx that's also in our history
                // If we're spending our own previous UTXO → we're the sender
                let spentOurUtxo = false;
                if (d.vin) {
                    for (const vin of d.vin) {
                        if (vin.txid && allTxs.has(vin.txid)) {
                            spentOurUtxo = true;
                            break;
                        }
                    }
                }

                if (spentOurUtxo) {
                    direction = 'sent';
                    amountITC = (sentToOthersSats / 100000000).toFixed(8);
                    dirBadge = `<span class="tx-badge-sent">↑ SENT</span>`;
                } else {
                    direction = 'received';
                    amountITC = (receivedSats / 100000000).toFixed(8);
                    dirBadge = `<span class="tx-badge-received">↓ RECEIVED</span>`;
                }
            }

            html += `
                <div class="card glass p-4 mb-4" style="padding: 14px;">
                    <div class="flex justify-between items-center">
                        <div>
                            ${dirBadge}
                            <a href="${explorer}/tx/${tx.tx_hash}" target="_blank"
                               class="text-sm" style="color: var(--cyan); text-decoration: none; font-family: monospace; margin-left: 6px;">
                                ${shortTxid} ↗
                            </a>
                            <br>
                            ${statusBadge}
                        </div>
                        <div class="text-right">
                            <p style="font-weight: 600; color: ${direction === 'sent' ? '#ef4444' : '#4ade80'}; font-size: 0.9rem;">
                                ${direction === 'sent' ? '-' : '+'}${amountITC}
                            </p>
                            <p class="text-sm text-muted">ITC</p>
                        </div>
                    </div>
                </div>
            `;
        }
        container.innerHTML = html;
    },

    // Core Logic
    async loadWalletData(mnemonic) {
        try {
            state.wallet = await loadWalletFromMnemonic(mnemonic);

            // Show primary address on dashboard
            const addr = state.wallet.address;
            document.getElementById('address-list').innerHTML = `
                <div class="address-item">
                    <span>Main Account</span>
                    <a href="https://explorer.interchained.org/address/${addr}" target="_blank"
                       class="addr text-sm" style="color: var(--cyan); text-decoration: none;">
                        ${addr.slice(0, 8)}...${addr.slice(-8)} ↗
                    </a>
                </div>
            `;

            this.switchView('dashboard');
        } catch (e) {
            console.error(e);
            alert("Failed to load wallet derivation limits.");
        }
    },

    async refreshBalance() {
        if (!state.wallet || !state.wallet.addresses) return;
        try {
            const allAddrs = state.wallet.addresses;

            // 1. Batch fetch all balances
            const balCalls = allAddrs.map(a => ({
                method: "blockchain.scripthash.get_balance",
                params: [a.scriptHash]
            }));

            // 2. Batch fetch all UTXOs
            const utxoCalls = allAddrs.map(a => ({
                method: "blockchain.scripthash.listunspent",
                params: [a.scriptHash]
            }));

            const res = await fetch('/api/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ calls: [...balCalls, ...utxoCalls] })
            });

            if (!res.ok) throw new Error("Batch fetch failed");
            const data = await res.json();
            const results = data.results;

            let totalConfirmed = 0;
            let totalUnconfirmed = 0;
            let allUtxos = [];

            // Half results are balances, half are utxos
            const half = allAddrs.length;

            allAddrs.forEach((addr, i) => {
                const bal = results[i] || { confirmed: 0, unconfirmed: 0 };
                totalConfirmed += bal.confirmed || 0;
                totalUnconfirmed += bal.unconfirmed || 0;
                addr.balance = (bal.confirmed || 0) + (bal.unconfirmed || 0);

                const utxosList = results[half + i] || [];
                // Attach the WIF key to each UTXO so we can sign it later
                const enrichedUtxos = utxosList.map(u => ({ ...u, wif: addr.wif }));
                allUtxos.push(...enrichedUtxos);
                addr.utxos = enrichedUtxos;
            });

            const networkTotal = totalConfirmed + totalUnconfirmed;

            // Clear pending if network caught up
            if (state.pendingOutgoing > 0 && networkTotal !== state.lastConfirmedBalance) {
                state.pendingOutgoing = 0;
            }

            state.balance = networkTotal;
            state.lastConfirmedBalance = networkTotal;
            state.utxos = allUtxos;

            // Update UI
            const displayBalance = Math.max(0, networkTotal - state.pendingOutgoing);
            const balanceEl = document.getElementById('total-balance');
            animateBalance(balanceEl, displayBalance / 100000000);

            const pendingEl = document.getElementById('pending-indicator');
            if (pendingEl) {
                if (state.pendingOutgoing > 0) {
                    pendingEl.classList.remove('hidden');
                    pendingEl.innerText = `⏳ -${(state.pendingOutgoing / 100000000).toFixed(8)} pending`;
                } else {
                    pendingEl.classList.add('hidden');
                }
            }

        } catch (e) {
            console.error("Failed fetching aggregate balance/utxos", e);
        }
    },

    async buildAndSignTx() {
        console.log('[Elara] buildAndSignTx called', { wallet: state.wallet, utxos: state.utxos, balance: state.balance });
        const toAddress = document.getElementById('send-address').value;
        const amountITC = parseFloat(document.getElementById('send-amount').value);
        const feeRate = parseFloat(document.getElementById('send-fee').value);

        if (!toAddress || !amountITC || !feeRate) {
            showToast('Please fill all fields', 'error');
            return;
        }

        try {
            // Amount in satoshis
            const amountSats = Math.floor(amountITC * 100000000);

            if (amountSats > state.balance) throw new Error("Insufficient funds");
            if (state.utxos.length === 0) throw new Error("No UTXOs available to spend.");

            // Create Hex TX using bitcoin-ops
            const { txHex, feeSats } = await createTransaction(
                state.wallet,
                state.utxos,
                toAddress,
                amountSats,
                feeRate
            );

            state.pendingTxHex = txHex;

            // Show preview
            document.getElementById('preview-to').innerText = toAddress;
            document.getElementById('preview-amount').innerText = amountITC.toFixed(8) + " ITC";
            document.getElementById('preview-fee').innerText = feeSats + " sats";
            document.getElementById('tx-preview').classList.remove('hidden');

        } catch (e) {
            console.error('[Elara] Transaction error:', e);
            showToast('Error: ' + e.message, 'error', 4000);
        }
    },

    async broadcastTx() {
        if (!state.pendingTxHex) return;

        // Grab the amount + fee before we clear pendingTxHex
        const sentAmount = parseFloat(document.getElementById('send-amount').value);
        const sentSats = Math.floor(sentAmount * 100000000);
        const feeSatsText = document.getElementById('preview-fee').innerText;
        const feeSats = parseInt(feeSatsText) || 141;

        const broadcastBtn = document.querySelector('#tx-preview .btn-primary');
        if (broadcastBtn) btnLoading(broadcastBtn, true);

        try {
            const res = await broadcastTx(state.pendingTxHex);
            if (res.txid) {
                state.pendingTxHex = null;
                document.getElementById('send-amount').value = '';
                state.pendingOutgoing += sentSats + feeSats;

                // Show success modal + confetti
                document.getElementById('success-txid').innerText = res.txid;
                document.getElementById('success-modal').classList.remove('hidden');
                spawnConfetti();
                showToast('Transaction broadcast! 🚀', 'success');

                await this.refreshBalance();
            } else {
                showToast('Broadcast failed', 'error', 4000);
            }
        } catch (e) {
            console.error('[Elara] Broadcast failed:', e);
            showToast('Broadcast failed: ' + e.message, 'error', 4000);
        } finally {
            if (broadcastBtn) btnLoading(broadcastBtn, false);
        }
    },

    closeSuccessModal() {
        document.getElementById('success-modal').classList.add('hidden');
        this.switchView('dashboard');
    },

    // ==========================================
    // ChangeNOW Swap Frontend Core Logic
    // ==========================================

    async loadSwapCurrencies() {
        try {
            const res = await fetch('/api/swap/currencies?active=true&flow=standard');
            if (!res.ok) throw new Error("Failed to fetch currencies");

            const currencies = await res.json();
            state.swapCurrencies = currencies.filter(c => !c.isFiat); // Exclude Fiat for now

            const fromSelect = document.getElementById('swap-from-currency');
            const toSelect = document.getElementById('swap-to-currency');

            let optionsHtml = '';
            // Put BTC, ETH, ITC on top
            const priority = ['btc', 'eth', 'itc', 'usdt', 'usdc'];
            const sorted = state.swapCurrencies.sort((a, b) => {
                const aIdx = priority.indexOf(a.ticker);
                const bIdx = priority.indexOf(b.ticker);
                if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                if (aIdx !== -1) return -1;
                if (bIdx !== -1) return 1;
                return a.ticker.localeCompare(b.ticker);
            });

            sorted.forEach(c => {
                const displayName = c.name ? `${c.name} (${c.ticker.toUpperCase()})` : c.ticker.toUpperCase();
                optionsHtml += `<option value="${c.ticker}:${c.network}">${displayName}</option>`;
            });

            fromSelect.innerHTML = optionsHtml;
            toSelect.innerHTML = optionsHtml;

            // Default selections: BTC and USDT (BSC)
            const btcOpt = Array.from(fromSelect.options).find(o => o.value.startsWith('btc:'));
            if (btcOpt) fromSelect.value = btcOpt.value;

            const usdtBscOpt = Array.from(toSelect.options).find(o => o.value === 'usdt:bsc');
            const usdtAnyOpt = Array.from(toSelect.options).find(o => o.value.startsWith('usdt:'));

            if (usdtBscOpt) toSelect.value = usdtBscOpt.value;
            else if (usdtAnyOpt) toSelect.value = usdtAnyOpt.value;

            this.updateSwapMinAmount();
            MarketChartManager.updatePairs();
        } catch (e) {
            console.error("Error loading currencies", e);
            showToast("Failed to load swap pairs.", "error");
        }
    },

    async updateSwapMinAmount() {
        const fromVal = document.getElementById('swap-from-currency').value;
        const toVal = document.getElementById('swap-to-currency').value;
        const minLabel = document.getElementById('swap-min-amount-label');
        const fromInput = document.getElementById('swap-from-amount');

        if (!fromVal || !toVal) return;

        const [from, fromNet] = fromVal.split(':');
        const [to, toNet] = toVal.split(':');

        minLabel.innerText = "Min: ...";
        try {
            const res = await fetch(`/api/swap/min-amount?fromCurrency=${from}&toCurrency=${to}&fromNetwork=${fromNet}&toNetwork=${toNet}&flow=standard`);
            if (res.ok) {
                const data = await res.json();
                if (data.minAmount) {
                    minLabel.innerText = `Min: ${data.minAmount}`;
                    fromInput.min = data.minAmount;

                    if (!fromInput.value || parseFloat(fromInput.value) < data.minAmount) {
                        fromInput.value = data.minAmount;
                        this.getSwapEstimate();
                    }
                }
            } else {
                minLabel.innerText = "Pair unavailable";
            }
        } catch (e) {
            minLabel.innerText = "Unknown";
        }
    },

    async getSwapEstimate() {
        const fromVal = document.getElementById('swap-from-currency').value;
        const toVal = document.getElementById('swap-to-currency').value;
        const amount = document.getElementById('swap-from-amount').value;
        const toInput = document.getElementById('swap-to-amount');
        const speedLabel = document.getElementById('swap-speed-label');

        if (!fromVal || !toVal || !amount || parseFloat(amount) <= 0) {
            toInput.value = '';
            return;
        }

        const [from, fromNet] = fromVal.split(':');
        const [to, toNet] = toVal.split(':');

        speedLabel.innerText = 'Calculating...';

        try {
            const res = await fetch(`/api/swap/estimate?fromCurrency=${from}&toCurrency=${to}&fromAmount=${amount}&fromNetwork=${fromNet}&toNetwork=${toNet}&flow=standard`);
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Route unavailable");
            }
            const data = await res.json();

            if (data.toAmount) {
                toInput.value = data.toAmount;
                speedLabel.innerText = '⚡ Instant (1 block)';
            } else {
                toInput.value = '';
                speedLabel.innerText = 'No route';
            }
        } catch (e) {
            toInput.value = '';
            speedLabel.innerText = 'Error';
            showToast(e.message, 'error');
        }
    },

    async createSwap() {
        const fromVal = document.getElementById('swap-from-currency').value;
        const toVal = document.getElementById('swap-to-currency').value;

        if (!fromVal || !toVal) return;
        const [fromC, fromNet] = fromVal.split(':');
        const [toC, toNet] = toVal.split(':');

        const amount = parseFloat(document.getElementById('swap-from-amount').value);
        const toAddr = document.getElementById('swap-to-address').value.trim();
        const refundAddr = document.getElementById('swap-refund-address').value.trim();
        const errMsg = document.getElementById('swap-error-msg');
        const btn = document.getElementById('swap-confirm-btn');

        errMsg.style.display = 'none';

        if (!amount || amount <= 0) {
            errMsg.innerText = "Enter a valid amount.";
            errMsg.style.display = 'block';
            return;
        }
        if (!toAddr) {
            errMsg.innerText = "Destination address is required.";
            errMsg.style.display = 'block';
            return;
        }

        btn.disabled = true;
        const originalText = btn.innerText;
        btn.innerText = "Processing...";

        try {
            const payload = {
                fromCurrency: fromC,
                toCurrency: toC,
                fromAmount: amount,
                address: toAddr,
                fromNetwork: fromNet,
                toNetwork: toNet,
                flow: "standard"
            };
            if (refundAddr) payload.refundAddress = refundAddr;

            const res = await fetch('/api/swap/transaction', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Swap execution failed.");
            }

            const data = await res.json();

            // Get existing or create new multiple active swaps list
            const activeStr = localStorage.getItem('elara_active_swaps') || '[]';
            let activeList = JSON.parse(activeStr);
            activeList = activeList.filter(s => s.id !== data.id); // Deduplicate
            activeList.push({
                id: data.id,
                fromAmount: payload.fromAmount,
                fromCurrency: payload.fromCurrency,
                toCurrency: payload.toCurrency,
                createdAt: new Date().toISOString()
            });
            localStorage.setItem('elara_active_swaps', JSON.stringify(activeList));

            state.swapActiveId = data.id;
            this.showSwapTracker(data);

        } catch (e) {
            this.showSwapError(e.message);
        } finally {
            btn.disabled = false;
            btn.innerText = originalText;
        }
    },

    showSwapError(rawMessage) {
        // Map ChangeNow error codes to human-friendly content
        const errorMap = {
            'not_valid_address': {
                title: 'Wrong Address Format',
                desc: 'The destination address doesn\'t match the selected network.',
                tip: '💡 Double-check you\'ve entered a valid address for the receiving currency. EVM addresses (0x...) only work for ETH, BNB, USDT (BSC/ETH) — not BTC.'
            },
            'out_of_range': {
                title: 'Amount Out of Range',
                desc: 'The swap amount is too small or too large for this pair.',
                tip: '💡 Try adjusting the amount. Check the minimum shown next to the send field.'
            },
            'pair_is_inactive': {
                title: 'Pair Unavailable',
                desc: 'This trading pair is temporarily suspended by the provider.',
                tip: '💡 Try a different pair or check back shortly.'
            },
            'deposit_too_small': {
                title: 'Amount Too Small',
                desc: 'The amount is below the minimum required for this swap.',
                tip: '💡 Increase the amount above the minimum shown next to the send field.'
            },
            'route_not_found': {
                title: 'No Route Available',
                desc: 'No exchange path exists for this pair right now.',
                tip: '💡 Try swapping through a common intermediate like USDT or BNB.'
            },
            'refund_address_required': {
                title: 'Refund Address Required',
                desc: 'This swap requires a refund address in case it fails.',
                tip: '💡 Fill in the optional Refund Address field above.'
            },
        };

        // Try to extract error code from the raw message
        let code = '';
        let matched = null;
        for (const key of Object.keys(errorMap)) {
            if (rawMessage && rawMessage.toLowerCase().includes(key.toLowerCase())) {
                code = key;
                matched = errorMap[key];
                break;
            }
        }

        const fallback = {
            title: 'Swap Failed',
            desc: rawMessage || 'An unexpected error occurred.',
            tip: '💡 Check your inputs and try again. If the issue persists, the pair may be temporarily unavailable.'
        };

        const { title, desc, tip } = matched || fallback;

        // Set modal content
        const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
        set('sem-title', title);
        set('sem-desc', desc);
        set('sem-tip', tip);
        set('sem-code', code ? `Error Code: ${code}` : '');
        set('sem-raw', '');

        // Show and animate
        const modal = document.getElementById('swap-error-modal');
        const card = modal?.querySelector('.swap-error-modal-card');
        if (!modal) return;
        modal.classList.remove('hidden');

        if (typeof gsap !== 'undefined' && card) {
            gsap.fromTo(modal, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
            gsap.fromTo(card,
                { scale: 0.85, opacity: 0, y: 30 },
                { scale: 1, opacity: 1, y: 0, duration: 0.45, ease: 'back.out(2)', delay: 0.05 }
            );
            // Shake icon
            const icon = document.getElementById('sem-icon');
            if (icon) {
                gsap.fromTo(icon,
                    { x: -8 },
                    { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)', delay: 0.3 }
                );
            }
        }
    },

    closeSwapErrorModal() {
        const modal = document.getElementById('swap-error-modal');
        if (!modal) return;
        if (typeof gsap !== 'undefined') {
            gsap.to(modal, {
                opacity: 0, duration: 0.2, ease: 'power2.in',
                onComplete: () => modal.classList.add('hidden')
            });
        } else {
            modal.classList.add('hidden');
        }
    },

    renderActiveSwaps() {
        const listStr = localStorage.getItem('elara_active_swaps') || '[]';
        let activeList = JSON.parse(listStr);
        state.activeSwaps = activeList;

        const container = document.getElementById('active-swaps-container');
        const listContainer = document.getElementById('active-swaps-list');

        if (activeList.length === 0) {
            listContainer.classList.add('hidden');
            return;
        }

        listContainer.classList.remove('hidden');
        let html = '';
        activeList.forEach(swap => {
            html += `
                <div class="active-swap-card" onclick="app.resumeSwapTracker('${swap.id}')">
                    <div>
                        <p class="text-sm font-bold text-white">${swap.fromAmount} ${swap.fromCurrency.toUpperCase()} → ${swap.toCurrency.toUpperCase()}</p>
                        <p class="text-xs text-muted">ID: ${swap.id.slice(0, 8)}...</p>
                    </div>
                    <div style="font-size: 1.2rem; color: var(--cyan);">→</div>
                </div>
            `;
        });
        container.innerHTML = html;
    },

    resumeSwapTracker(id) {
        if (id) {
            state.swapActiveId = id;
            // Show placeholder while polling
            this.showSwapTracker({ id, fromAmount: '...', fromCurrency: '...', payinAddress: '...', status: 'loading' });
            this.pollSwapStatus();
        }
    },

    closeSwapTracker() {
        if (state.swapTrackerInterval) clearInterval(state.swapTrackerInterval);
        if (state.swapCountdownInterval) clearInterval(state.swapCountdownInterval);
        document.getElementById('swap-tracker-container').classList.add('hidden');
        document.getElementById('swap-setup-container').classList.remove('hidden');

        // Ensure the active swaps list updates if we completed/removed one
        this.renderActiveSwaps();
    },

    showSwapTracker(swapData) {
        document.getElementById('swap-setup-container').classList.add('hidden');
        document.getElementById('swap-tracker-container').classList.remove('hidden');

        // Provide a fallback status if the creation API response omits it
        swapData.status = swapData.status || 'new';

        // Provide a faux 10-minute deadline if the standard flow omits `validUntil`
        if (!swapData.validUntil && ['new', 'waiting'].includes(swapData.status)) {
            swapData.validUntil = new Date(new Date().getTime() + 10 * 60000).toISOString();
        }

        // Extract values handling both POST and GET response structures
        const amount = swapData.expectedAmountFrom || swapData.fromAmount || swapData.amountFrom || '';
        const currency = swapData.fromCurrency ? swapData.fromCurrency.toUpperCase() : '';
        const address = swapData.payinAddress || '';

        document.getElementById('swap-deposit-amount').innerText = `${amount} ${currency}`;
        document.getElementById('swap-deposit-address').innerText = address;

        const qrContainer = document.getElementById('swap-qrcode');
        qrContainer.innerHTML = '';
        if (address && address !== '...') {
            new QRCode(qrContainer, {
                text: address,
                width: 160,
                height: 160,
                colorDark: "#000000",
                colorLight: "#ffffff",
                correctLevel: QRCode.CorrectLevel.M
            });
        }

        const timerEl = document.getElementById('swap-deposit-timer');
        if (state.swapCountdownInterval) clearInterval(state.swapCountdownInterval);

        if (swapData.validUntil && ['new', 'waiting'].includes(swapData.status)) {
            timerEl.classList.remove('hidden');
            const targetTime = new Date(swapData.validUntil).getTime();

            state.swapCountdownInterval = setInterval(() => {
                const now = new Date().getTime();
                let diff = targetTime - now;

                if (diff <= 0) {
                    diff = 0;
                    clearInterval(state.swapCountdownInterval);
                    timerEl.innerText = "Expired";
                } else {
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                    timerEl.innerText = `Expires in: ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                }
            }, 1000);

            // Trigger immediately once so it doesn't say --:-- for 1s
            const now = new Date().getTime();
            let diff = targetTime - now;
            if (diff > 0) {
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                timerEl.innerText = `Expires in: ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            } else {
                timerEl.innerText = "Expired";
            }
        } else {
            timerEl.classList.add('hidden');
        }

        this.updateSwapTrackerStatus(swapData.status);

        // ── Populate Trade Receipt ──
        this._populateReceipt(swapData);

        // Start polling
        if (state.swapTrackerInterval) clearInterval(state.swapTrackerInterval);
        state.swapTrackerInterval = setInterval(() => this.pollSwapStatus(), 15000);
    },

    async _populateReceipt(swapData) {
        const from = swapData.fromCurrency ? swapData.fromCurrency.toUpperCase() : '';
        const to = swapData.toCurrency ? swapData.toCurrency.toUpperCase() : '';
        const fromAmt = parseFloat(swapData.expectedAmountFrom || swapData.fromAmount || swapData.amountFrom || 0);
        const toAmt = parseFloat(swapData.expectedAmountTo || swapData.toAmount || swapData.amountTo || 0);
        const swapId = swapData.id || '—';
        const now = new Date();
        const ts = now.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

        // Fill static fields
        const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
        set('receipt-sent', `${fromAmt} ${from}`);
        set('receipt-receive', toAmt > 0 ? `${toAmt} ${to}` : `— ${to}`);
        set('receipt-id', swapId);
        set('receipt-timestamp', ts);
        set('receipt-status', 'PENDING');
        set('receipt-usdt', 'Fetching...');

        // Rate
        if (fromAmt > 0 && toAmt > 0) {
            const rate = (toAmt / fromAmt).toFixed(6);
            set('receipt-rate', `1 ${from} = ${rate} ${to}`);
        } else {
            set('receipt-rate', '—');
        }

        // Show receipt with GSAP slide-in
        const receipt = document.getElementById('swap-receipt');
        if (receipt) {
            receipt.classList.remove('hidden');
            if (typeof gsap !== 'undefined') {
                gsap.fromTo(receipt,
                    { opacity: 0, y: 24 },
                    { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', delay: 0.2 }
                );
            }
        }

        // ── Fetch live USDT value at capture ──
        // Strategy: get price of fromCurrency in USDT, multiply by fromAmt
        try {
            const fromTicker = from === 'BTC' ? 'BTCUSDT'
                : from === 'ETH' ? 'ETHUSDT'
                    : from === 'BNB' ? 'BNBUSDT'
                        : `${from}USDT`;

            const priceRes = await fetch(`https://api.binance.us/api/v3/ticker/price?symbol=${fromTicker}`);
            if (priceRes.ok) {
                const priceData = await priceRes.json();
                const usdtPrice = parseFloat(priceData.price) * fromAmt;
                set('receipt-usdt', `≈ $${usdtPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`);
            } else {
                // Fallback: try toAmt if to is USDT
                if (to === 'USDT' && toAmt > 0) {
                    set('receipt-usdt', `≈ $${toAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`);
                } else {
                    set('receipt-usdt', '—');
                }
            }
        } catch {
            if (to === 'USDT' && toAmt > 0) {
                set('receipt-usdt', `≈ $${toAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`);
            } else {
                set('receipt-usdt', '—');
            }
        }
    },

    async pollSwapStatus() {
        if (!state.swapActiveId) return;
        try {
            const res = await fetch(`/api/swap/status/${state.swapActiveId}`);
            if (res.ok) {
                const data = await res.json();

                // If no validUntil is provided (standard flows), enforce a 2-hour local timeout
                if (!data.validUntil && ['new', 'waiting'].includes(data.status)) {
                    let activeList = JSON.parse(localStorage.getItem('elara_active_swaps') || '[]');
                    let localSwap = activeList.find(s => s.id === data.id);
                    if (localSwap && localSwap.createdAt) {
                        const age = new Date().getTime() - new Date(localSwap.createdAt).getTime();
                        if (age > 2 * 60 * 60 * 1000) { // 2 hours
                            data.status = 'failed';
                        }
                    }
                }

                // If we resumed with placeholders, hydrate UI on first poll
                const amountEl = document.getElementById('swap-deposit-amount');
                if (amountEl.innerText.includes('...')) {
                    this.showSwapTracker(data);
                }

                this.updateSwapTrackerStatus(data.status);

                // Stop polling if terminal state
                if (['finished', 'failed', 'refunded', 'expired'].includes(data.status)) {
                    clearInterval(state.swapTrackerInterval);
                    if (state.swapCountdownInterval) clearInterval(state.swapCountdownInterval);

                    // Remove from active swaps tracking
                    const activeStr = localStorage.getItem('elara_active_swaps') || '[]';
                    let activeList = JSON.parse(activeStr);
                    activeList = activeList.filter(s => s.id !== state.swapActiveId);
                    localStorage.setItem('elara_active_swaps', JSON.stringify(activeList));

                    state.swapActiveId = null;
                    if (data.status === 'finished') {
                        showToast('Swap Complete! Funds sent to destination.', 'success', 6000);
                        spawnConfetti();
                    } else {
                        showToast(`Swap ended with status: ${data.status}`, 'error', 6000);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
    },

    updateSwapTrackerStatus(status) {
        const statusEl = document.getElementById('swap-tracker-status');
        const heading = document.getElementById('swap-tracker-heading');

        const statusMessages = {
            'new': 'Preparing your swap...',
            'waiting': 'Awaiting your deposit...',
            'confirming': 'Deposit detected! Confirming on-chain...',
            'exchanging': 'Confirmed. Executing exchange...',
            'sending': 'Exchange complete. Sending to you...',
            'finished': 'Swap Complete! 🎉',
            'failed': 'Swap Failed',
            'refunded': 'Swap Refunded',
            'expired': 'Deposit window expired'
        };

        // Status → which step index is ACTIVE (0-indexed)
        const statusStepMap = {
            'new': 0,
            'waiting': 0,
            'confirming': 1,
            'exchanging': 2,
            'sending': 3,
            'finished': 4,
        };

        const stepIds = ['step-waiting', 'step-confirming', 'step-exchanging', 'step-sending', 'step-finished'];
        const fillIds = ['fill-1', 'fill-2', 'fill-3', 'fill-4'];

        const currentStep = statusStepMap[status] ?? -1;

        // Update text
        if (statusEl) statusEl.textContent = statusMessages[status] || `Status: ${status}`;

        // Stop countdown + hide timer when past deposit stage
        if (!['new', 'waiting'].includes(status)) {
            if (state.swapCountdownInterval) {
                clearInterval(state.swapCountdownInterval);
                state.swapCountdownInterval = null;
            }
            const timerEl = document.getElementById('swap-deposit-timer');
            if (timerEl) timerEl.classList.add('hidden');
        }

        if (['failed', 'expired', 'refunded'].includes(status)) {
            // ── Terminal Failure ──
            if (heading) heading.textContent = statusMessages[status] || 'Swap Failed';

            const overlay = document.getElementById('swap-failed-overlay');
            if (overlay && overlay.classList.contains('hidden')) {
                overlay.classList.remove('hidden');
                overlay.style.display = 'flex';

                const titleEl = document.getElementById('failed-overlay-title');
                const descEl = document.getElementById('failed-overlay-desc');
                if (status === 'expired') {
                    titleEl.textContent = 'Swap Expired';
                    descEl.textContent = 'The deposit window closed before funds were received.';
                } else if (status === 'refunded') {
                    titleEl.textContent = 'Swap Refunded';
                    descEl.textContent = 'Funds have been returned to your refund address.';
                } else {
                    titleEl.textContent = 'Swap Failed';
                    descEl.textContent = 'An error occurred with the swap provider.';
                }

                if (typeof gsap !== 'undefined') {
                    const tl = gsap.timeline();
                    tl.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' })
                        .to('#failed-icon-svg', { opacity: 1, scale: 1.2, duration: 0.4, ease: 'back.out(1.7)' })
                        .to('#failed-icon-svg', { scale: 1, duration: 0.2, ease: 'power1.inOut' })
                        .to('#failed-overlay-title', { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '-=0.2')
                        .to('#failed-overlay-desc', { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '-=0.1')
                        .to('#failed-overlay-btn', { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '-=0.1');
                }
            }
            return;
        }

        // ── Animate step pipeline ──
        stepIds.forEach((id, i) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.classList.remove('active', 'done', 'finished');

            if (i < currentStep) {
                el.classList.add('done');
            } else if (i === currentStep) {
                if (status === 'finished') {
                    el.classList.add('finished');
                } else {
                    el.classList.add('active');
                }
                // GSAP spring animation on newly activated node
                if (typeof gsap !== 'undefined') {
                    const node = el.querySelector('.step-node');
                    gsap.fromTo(node,
                        { scale: 0.6, opacity: 0.3 },
                        { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(2)' }
                    );
                }
            }
        });

        // Fill connector lines
        fillIds.forEach((id, i) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.style.width = i < currentStep ? '100%' : '0%';
        });

        // ── QR Panel Visibility ──
        const panel = document.getElementById('swap-deposit-details-panel');
        if (panel) {
            if (['new', 'waiting'].includes(status)) {
                // Show QR while waiting for deposit
                panel.classList.remove('collapsing');
            } else {
                // Collapse QR once deposit detected — step animation is the hero
                panel.classList.add('collapsing');
            }
        }

        // ── Heading update ──
        const headings = {
            'new': 'Preparing Swap',
            'waiting': 'Awaiting Deposit',
            'confirming': 'Confirming Deposit',
            'exchanging': 'Executing Exchange',
            'sending': 'Sending Funds',
            'finished': 'Swap Complete! 🎉',
        };
        if (heading) heading.textContent = headings[status] || 'Processing Swap';

        // ── Finished: Green burst + confetti ──
        if (status === 'finished' && typeof gsap !== 'undefined') {
            const ring = document.querySelector('#step-finished .step-ring');
            if (ring) {
                gsap.to(ring, { boxShadow: '0 0 40px rgba(74, 222, 128, 0.9)', duration: 0.6, ease: 'power2.out' });
            }
            spawnConfetti();
        }

        // ── Update receipt status pill ──
        const pill = document.getElementById('receipt-status');
        if (pill) {
            const pillMap = {
                'new': ['PENDING', ''],
                'waiting': ['PENDING', ''],
                'confirming': ['CONFIRMING', ''],
                'exchanging': ['EXCHANGING', ''],
                'sending': ['SENDING', ''],
                'finished': ['COMPLETE', 'complete'],
                'failed': ['FAILED', 'failed'],
                'expired': ['EXPIRED', 'failed'],
                'refunded': ['REFUNDED', 'failed'],
            };
            const [label, cls] = pillMap[status] || ['PENDING', ''];
            pill.textContent = label;
            pill.className = 'receipt-value receipt-status-pill' + (cls ? ` ${cls}` : '');
        }
    },

    // Logout / Lock Wallet
    logout() {
        if (state.pollingInterval) clearInterval(state.pollingInterval);
        state.wallet = null;
        state.utxos = [];
        state.balance = 0;
        state.pendingOutgoing = 0;
        showToast('Wallet locked', 'info');
        this.switchView('setup');
        // Show the correct auth state
        document.getElementById('auth-state-new').classList.add('hidden');
        document.getElementById('auth-state-encrypt').classList.add('hidden');
        const stored = localStorage.getItem('elara_keystore');
        if (stored) {
            document.getElementById('auth-state-login').classList.remove('hidden');
        } else {
            document.getElementById('auth-state-new').classList.remove('hidden');
        }
    },

    checkLoginState() {
        const storedKeys = localStorage.getItem('elara_keystore');
        if (storedKeys) {
            document.getElementById('auth-state-new').classList.add('hidden');
            document.getElementById('auth-state-login').classList.remove('hidden');
        }
    },

    async pollAllActiveSwapsBackground() {
        const listStr = localStorage.getItem('elara_active_swaps') || '[]';
        let activeList = JSON.parse(listStr);
        if (activeList.length === 0) return;

        let listChanged = false;

        for (let i = activeList.length - 1; i >= 0; i--) {
            const swap = activeList[i];
            // Skip the one that the foreground tracker is already polling natively
            if (swap.id === state.swapActiveId) continue;

            try {
                const res = await fetch(`/api/swap/status/${swap.id}`);
                if (res.ok) {
                    const data = await res.json();

                    // If no validUntil is provided, enforce a 2-hour local timeout
                    if (!data.validUntil && ['new', 'waiting'].includes(data.status)) {
                        if (swap.createdAt) {
                            const age = new Date().getTime() - new Date(swap.createdAt).getTime();
                            if (age > 2 * 60 * 60 * 1000) { // 2 hours
                                data.status = 'failed';
                            }
                        }
                    }

                    if (['finished', 'failed', 'refunded', 'expired'].includes(data.status)) {
                        activeList.splice(i, 1);
                        listChanged = true;
                        if (data.status === 'finished') {
                            showToast(`Swap ${swap.id.slice(0, 5)}... Complete!`, 'success', 8000);
                            spawnConfetti();
                        } else {
                            showToast(`Swap ${swap.id.slice(0, 5)}... ended: ${data.status}`, 'error', 8000);
                        }
                    }
                }
            } catch (e) {
                console.error("Background poll error on", swap.id, e);
            }
        }

        if (listChanged) {
            localStorage.setItem('elara_active_swaps', JSON.stringify(activeList));
            if (state.currentView === 'swap') {
                this.renderActiveSwaps();
            }
        }
    }
};

// ===== Live Market Chart Integration =====
window.MarketChartManager = {
    chart: null,
    seriesFrom: null,
    seriesTo: null,
    wsFrom: null,
    wsTo: null,
    stablecoins: ['usdt', 'usdc', 'busd', 'dai', 'tusd', 'fdusd'],

    init() {
        if (typeof LightweightCharts === 'undefined') {
            setTimeout(() => this.init(), 100);
            return;
        }

        const container = document.getElementById('swap-chart-container');
        if (!container) return;

        // Ensure we don't duplicate charts if init is called multiple times
        if (this.chart) return;

        document.getElementById('swap-chart-wrapper').classList.remove('hidden');

        this.chart = LightweightCharts.createChart(container, {
            layout: {
                background: { type: 'solid', color: 'transparent' },
                textColor: '#9ca3af',
            },
            grid: {
                vertLines: { color: 'rgba(255, 255, 255, 0.05)' },
                horzLines: { color: 'rgba(255, 255, 255, 0.05)' },
            },
            rightPriceScale: {
                mode: 0, // Normal (Absolute prices)
                autoScale: true,
                borderVisible: false,
            },
            leftPriceScale: {
                visible: true,
                mode: 0, // Normal (Absolute prices)
                autoScale: true,
                borderVisible: false,
            },
            timeScale: {
                borderVisible: false,
                timeVisible: true,
                secondsVisible: false,
            },
            crosshair: {
                vertLine: {
                    color: 'rgba(232, 185, 35, 0.4)',
                    width: 1,
                    style: 1,
                    labelBackgroundColor: '#1f2937',
                },
                horzLine: {
                    color: 'rgba(232, 185, 35, 0.4)',
                    width: 1,
                    style: 1,
                    labelBackgroundColor: '#1f2937',
                },
            }
        });

        // Resize observer to keep chart responsive
        new ResizeObserver(entries => {
            if (entries.length === 0 || entries[0].target !== container) { return; }
            const newRect = entries[0].contentRect;
            this.chart.applyOptions({ height: newRect.height, width: newRect.width });
        }).observe(container);

        this.updatePairs();
    },

    updatePairs() {
        if (!this.chart) return;

        const fromVal = document.getElementById('swap-from-currency').value;
        const toVal = document.getElementById('swap-to-currency').value;
        if (!fromVal || !toVal) return;

        const fromTicker = fromVal.split(':')[0].toLowerCase();
        const toTicker = toVal.split(':')[0].toLowerCase();

        // Teardown existing connections and series
        if (this.wsFrom) { this.wsFrom.close(); this.wsFrom = null; }
        if (this.wsTo) { this.wsTo.close(); this.wsTo = null; }
        if (this.seriesFrom) { this.chart.removeSeries(this.seriesFrom); this.seriesFrom = null; }
        if (this.seriesTo) { this.chart.removeSeries(this.seriesTo); this.seriesTo = null; }

        const fromIsStable = this.stablecoins.includes(fromTicker);
        const toIsStable = this.stablecoins.includes(toTicker);

        const skeleton = document.getElementById('swap-chart-skeleton');
        if (skeleton) skeleton.style.display = 'block';

        let legendHTML = '';
        let hasSecondary = false;

        // Axis logic:
        // 1. Both non-stable: PAY=Left, GET=Right
        // 2. Only PAY is non-stable: PAY=Right
        // 3. Only GET is non-stable: GET=Right

        if (!fromIsStable && !toIsStable && fromTicker !== toTicker) {
            // Case 1: Complex pair (e.g. BTC/ETH)
            this.seriesFrom = this.chart.addLineSeries({ color: '#4ade80', lineWidth: 2, priceScaleId: 'left' });
            legendHTML += `<span style="color: #4ade80; font-weight: bold;">[PAY] ${fromTicker.toUpperCase()}/USDT</span>`;
            this._connectSocket(fromTicker, this.seriesFrom);

            this.seriesTo = this.chart.addLineSeries({ color: '#E8B923', lineWidth: 2, priceScaleId: 'right' });
            legendHTML += `<span style="color: #E8B923; font-weight: bold;">[GET] ${toTicker.toUpperCase()}/USDT</span>`;
            this._connectSocket(toTicker, this.seriesTo);

            this.chart.priceScale('left').applyOptions({ visible: true });
            this.chart.priceScale('right').applyOptions({ visible: true });
        } else if (!fromIsStable) {
            // Case 2: Only Paying asset has price (e.g. BTC -> USDT)
            this.seriesFrom = this.chart.addLineSeries({ color: '#4ade80', lineWidth: 2, priceScaleId: 'right' });
            legendHTML += `<span style="color: #4ade80; font-weight: bold;">${fromTicker.toUpperCase()}/USDT</span>`;
            this._connectSocket(fromTicker, this.seriesFrom);

            this.chart.priceScale('left').applyOptions({ visible: false });
            this.chart.priceScale('right').applyOptions({ visible: true });
        } else if (!toIsStable) {
            // Case 3: Only Getting asset has price (e.g. USDT -> BTC)
            this.seriesTo = this.chart.addLineSeries({ color: '#E8B923', lineWidth: 2, priceScaleId: 'right' });
            legendHTML += `<span style="color: #E8B923; font-weight: bold;">${toTicker.toUpperCase()}/USDT</span>`;
            this._connectSocket(toTicker, this.seriesTo);

            this.chart.priceScale('left').applyOptions({ visible: false });
            this.chart.priceScale('right').applyOptions({ visible: true });
        } else {
            // Case 4: Stable to Stable (e.g. USDT -> USDC)
            legendHTML += `<span class="text-muted">Stablecoin Swap</span>`;
            this.chart.priceScale('left').applyOptions({ visible: false });
            this.chart.priceScale('right').applyOptions({ visible: false });
            if (skeleton) skeleton.style.display = 'none';
        }

        document.getElementById('swap-chart-legend').innerHTML = legendHTML;
    },

    async _fetchHistory(ticker, series) {
        try {
            // Map common tickers to Binance format (e.g. BTC -> BTCUSDT)
            let symbol = ticker.toUpperCase();
            if (symbol === 'BTC') symbol = 'BTCUSDT';
            else if (symbol === 'ETH') symbol = 'ETHUSDT';
            else if (!symbol.endsWith('USDT')) symbol += 'USDT';

            const res = await fetch(`https://api.binance.us/api/v3/klines?symbol=${symbol}&interval=1m&limit=60`);
            if (!res.ok) throw new Error("History fetch failed");
            const data = await res.json();

            if (Array.isArray(data) && data.length > 0) {
                const points = data.map(k => ({
                    time: Math.floor(k[0] / 1000),
                    value: parseFloat(k[4])
                }));
                series.setData(points);
                return {
                    price: parseFloat(data[data.length - 1][4]),
                    time: Math.floor(data[data.length - 1][0] / 1000)
                };
            }
        } catch (e) {
            console.warn(`[Elara] History fetch failed for ${ticker}:`, e.message);
        }
        return null;
    },

    _getKrakenSymbol(ticker) {
        const t = ticker.toUpperCase();
        const map = {
            'BTC': 'XBT/USD',
            'ETH': 'ETH/USD',
            'USDT': 'USDT/USD',
            'USDC': 'USDC/USD',
            'LTC': 'LTC/USD',
            'SOL': 'SOL/USD',
            'DOGE': 'DOGE/USD',
            'ADA': 'ADA/USD',
            'XRP': 'XRP/USD'
        };
        // Fallback to TICKER/USD which works for 90% of Kraken pairs
        return map[t] || `${t}/USD`;
    },

    async _connectSocket(ticker, series) {
        let lastPrice = 0;
        let lastUpdateTime = 0;
        let jitterInterval = null;
        let winnerFound = false;

        // 1. Pre-load History
        const history = await this._fetchHistory(ticker, series);
        if (history) {
            lastPrice = history.price;
            lastUpdateTime = history.time;
            const skeleton = document.getElementById('swap-chart-skeleton');
            if (skeleton) skeleton.style.display = 'none';
        }

        const onData = (price, time, source) => {
            // Lock in the winner and shut down the side stream
            if (!winnerFound) {
                winnerFound = true;
                console.log(`[Elara] ${ticker} feed locked to: ${source}`);
                if (source === 'Binance') {
                    if (kWS) { kWS.onmessage = null; kWS.close(); }
                } else {
                    if (bWS) { bWS.onmessage = null; bWS.close(); }
                }
            }

            // Sync: Real data always overrides jitter, but time must go forward
            let now = Math.floor(Date.now() / 1000);
            let useTime = Math.max(now, lastUpdateTime + 1);

            lastPrice = price;
            lastUpdateTime = useTime;
            series.update({ time: useTime, value: price });

            const skeleton = document.getElementById('swap-chart-skeleton');
            if (skeleton) skeleton.style.display = 'none';

            if (!jitterInterval && lastPrice > 0) {
                jitterInterval = setInterval(() => {
                    if (lastPrice === 0) return;
                    const noise = 1 + (Math.random() * 0.0004 - 0.0002);
                    let jTime = Math.max(Math.floor(Date.now() / 1000), lastUpdateTime + 1);
                    lastUpdateTime = jTime;
                    series.update({ time: jTime, value: lastPrice * noise });
                }, 350);
            }
        };

        // 2. Start Simultaneous Race
        const bSymbol = (ticker.toLowerCase() === 'btc' ? 'btcusdt' :
            ticker.toLowerCase() === 'eth' ? 'ethusdt' :
                `${ticker.toLowerCase()}usdt`);
        const bWS = new WebSocket(`wss://stream.binance.us:9443/ws/${bSymbol}@kline_1m`);

        const kSymbol = this._getKrakenSymbol(ticker);
        const kWS = new WebSocket(`wss://ws.kraken.com/v2`);

        bWS.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.k) onData(parseFloat(data.k.c), Math.floor(data.k.t / 1000), 'Binance');
            } catch (err) { }
        };

        kWS.onopen = () => {
            kWS.send(JSON.stringify({
                method: "subscribe",
                params: { channel: "ohlc", symbol: [kSymbol], interval: 1 }
            }));
        };

        kWS.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data);
                if (msg.channel === 'ohlc' && msg.type === 'update' && msg.data) {
                    const snap = msg.data[0];
                    const time = Math.floor(new Date(snap.timestamp).getTime() / 1000);
                    onData(parseFloat(snap.close), time, 'Kraken');
                }
            } catch (err) { }
        };

        const totalCleanup = () => {
            if (jitterInterval) clearInterval(jitterInterval);
            if (bWS) { bWS.onmessage = null; bWS.close(); }
            if (kWS) { kWS.onmessage = null; kWS.close(); }
        };

        if (series === this.seriesFrom) this.wsFrom = { close: totalCleanup };
        else if (series === this.seriesTo) this.wsTo = { close: totalCleanup };
    }
};

// Modules are deferred, so DOM is ready. Initialize immediately.
app.checkLoginState();
setInterval(() => app.pollAllActiveSwapsBackground(), 20000); // Background poller every 20s

// Dismiss splash screen
const splash = document.getElementById('splash-screen');
if (splash) {
    splash.classList.add('fade-out');
    setTimeout(() => splash.remove(), 700);
}
